// Every page the crawl found, by name — not just the ones with cases so far.
// ProjectCasesCard's "By page" tab only ever lists pages that already HAVE
// cases, so during a batched generate run most of what was crawled was
// invisible until the whole run finished. This is the plain inventory: all
// crawled pages, each annotated with its case count (0 = not generated yet),
// live-updating the same way the rest of the screen does.
import { useMemo, useState } from "react";
import { FileText, Search } from "lucide-react";
import { cn } from "@/lib/cn";

const norm = (u) => String(u || "").replace(/\/+$/, "").toLowerCase();

export default function CrawledPagesList({ pages, byPage, busy }) {
  const [q, setQ] = useState("");

  const rows = useMemo(() => {
    const countByUrl = new Map();
    for (const p of byPage || []) {
      countByUrl.set(norm(p.page_url), p.count || 0);
    }
    return (pages || []).map((p) => ({
      url: p.url,
      title: p.title || p.url,
      count: countByUrl.get(norm(p.url)) || 0,
    }));
  }, [pages, byPage]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => r.title.toLowerCase().includes(needle)
      || r.url.toLowerCase().includes(needle));
  }, [rows, q]);

  if (!rows.length) return null;

  const withCases = rows.filter((r) => r.count > 0).length;

  return (
    <div className="card overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-5 py-4 dark:border-white/10">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
            <FileText size={16} className="text-brand-500" /> Crawled pages
            <span className="text-slate-400">({rows.length})</span>
          </h3>
          <p className="mt-0.5 text-xs text-slate-400">
            {withCases} of {rows.length} page(s) have test cases so far
            {busy ? " — updating as generation continues…" : "."}
          </p>
        </div>
        <div className="relative w-full max-w-xs">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search pages…"
            className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
          />
        </div>
      </div>
      <ul className="max-h-80 divide-y divide-slate-100 overflow-y-auto dark:divide-white/5">
        {filtered.map((r) => (
          <li key={r.url} className="flex items-center justify-between gap-3 px-5 py-2.5">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">{r.title}</p>
              <p className="truncate text-xs text-slate-400">{r.url}</p>
            </div>
            <span className={cn(
              "shrink-0 rounded-full px-2.5 py-1 text-xs font-medium",
              r.count > 0
                ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400"
                : "bg-slate-100 text-slate-500 dark:bg-white/5 dark:text-slate-400",
            )}>
              {r.count > 0 ? `${r.count} case${r.count === 1 ? "" : "s"}` : "pending"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
